import {
  forecastDateKey,
  forecastTime,
  upcomingForecast,
  precipitationLabel,
} from '../utils/forecast';

export default function LocalForecast({ area, forecast, compact = false }) {
  const { current, data, loading, fetchedAt, refresh } = forecast;
  const offset = data?.tzOffset ?? current?.tzOffset ?? 0;
  const slots = upcomingForecast(data?.hourly);
  const today = forecastDateKey(new Date(), offset);

  return (
    <section
      aria-label={`Weather near ${area.city}`}
      className="overflow-hidden rounded-2xl border border-stone-200 bg-white"
    >
      <div className="px-4 pb-4 pt-4 sm:px-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">
              {compact ? 'Weather nearby' : 'Before you head out'}
            </h2>
            <p className="mt-1 text-xs text-stone-500">{area.city} forecast</p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="rounded-lg px-2 py-1 text-xs font-medium text-stone-600 underline underline-offset-4 disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Refresh'}
          </button>
        </div>
        {loading && !current && !data ? (
          <p role="status" className="mt-5 text-sm text-stone-500">
            Checking the local forecast...
          </p>
        ) : (
          <>
            {current ? (
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                <p className="text-3xl font-medium tracking-tight">
                  {current.temp}&deg;F
                </p>
                <div className="text-sm leading-relaxed text-stone-600">
                  <p className="capitalize">{current.description}</p>
                  <p>
                    Feels like {current.feelsLike}&deg;F{' '}
                    <span aria-hidden="true">&middot;</span> Wind{' '}
                    {current.windSpeed} mph
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-stone-600">
                Current conditions are unavailable. You can still browse places.
              </p>
            )}

            {slots.length > 0 ? (
              <details className="mt-4" open={compact ? undefined : true}>
                <summary className="cursor-pointer text-sm font-medium">
                  Next 24 hours{' '}
                  <span className="font-normal text-stone-500">
                    &middot; every 3 hours
                  </span>
                </summary>
                <div
                  className="mt-3 overflow-x-auto rounded-xl border border-stone-200"
                  role="region"
                  aria-label="Scrollable forecast"
                  tabIndex={0}
                >
                  <table className="w-full border-collapse text-center text-sm">
                    <thead className="bg-[#f4f6f1]">
                      <tr>
                        <th
                          scope="col"
                          className="sticky left-0 z-10 bg-[#f4f6f1] px-3 py-3 text-left text-xs font-medium"
                        >
                          Local time
                        </th>
                        {slots.map((slot) => (
                          <th
                            key={slot.time.toISOString()}
                            scope="col"
                            className="min-w-[86px] px-2 py-3 font-medium"
                          >
                            <span className="block text-xs font-normal text-stone-500">
                              {forecastDateKey(slot.time, offset) === today
                                ? 'Today'
                                : forecastTime(slot.time, offset, {
                                    weekday: 'short',
                                  })}
                            </span>
                            {forecastTime(slot.time, offset)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      <tr>
                        <th
                          scope="row"
                          className="sticky left-0 z-10 bg-white px-3 py-3 text-left text-xs font-medium"
                        >
                          Temp
                        </th>
                        {slots.map((slot) => (
                          <td
                            key={slot.time.toISOString()}
                            className="px-2 py-3 font-semibold"
                            title={slot.description}
                          >
                            {slot.temp}&deg;F
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          scope="row"
                          className="sticky left-0 z-10 bg-white px-3 py-3 text-left text-xs font-medium"
                        >
                          Precip. chance
                        </th>
                        {slots.map((slot) => (
                          <td
                            key={slot.time.toISOString()}
                            className="px-2 py-3 text-[#416a78]"
                          >
                            {precipitationLabel(slot.pop)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th
                          scope="row"
                          className="sticky left-0 z-10 bg-white px-3 py-3 text-left text-xs font-medium"
                        >
                          Wind
                        </th>
                        {slots.map((slot) => (
                          <td
                            key={slot.time.toISOString()}
                            className="whitespace-nowrap px-2 py-3 text-stone-600"
                          >
                            {slot.windSpeed} mph
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-stone-500">
                  Scroll for later hours. Precipitation includes rain or snow.
                  Times are local to the selected area.
                </p>
              </details>
            ) : (
              <p className="mt-3 text-sm text-stone-500">
                The upcoming forecast is unavailable. Try refreshing in a
                moment.
              </p>
            )}

            {fetchedAt && (current || slots.length > 0) && (
              <p className="mt-3 text-xs text-stone-500">
                OpenWeather &middot; Retrieved{' '}
                {forecastTime(fetchedAt, offset, {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                . Area forecast, not trail or river conditions.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
